import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { SERVER_URL } from '../services/postService';
import { getUser, isLoggedIn } from '../services/authService';

const VERDICT_COLORS = {
  'Very Likely Happening': 'bg-green-500/20 text-green-400',
  'Likely Happening': 'bg-cyan-500/20 text-cyan-400',
  'Unconfirmed': 'bg-yellow-500/20 text-yellow-400',
  'Likely False': 'bg-red-500/20 text-red-400',
  'Awaiting Verification': 'bg-gray-500/20 text-gray-300',
};

function ChatBot() {
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'bot',
      text: 'Hi! Ask me what is happening in any area, or whether a report is real. I answer only from reports posted on LocalLens in the last 7 days.',
    },
  ]);
  const endRef = useRef(null);

  const city = isLoggedIn() ? (getUser()?.city || '').trim() : '';

  useEffect(() => {
    if (open && endRef.current) {
      endRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open, sending]);

  // the chat button is not needed on the login page
  if (location.pathname === '/login') return null;

  const suggestions = [
    `What's happening in ${city || 'my area'}?`,
    'Any emergencies today?',
    'Any high priority reports?',
  ];

  const send = async (text) => {
    const question = (text !== undefined ? text : input).trim();
    if (!question || sending) return;

    // the previous question lets "is it real?" make sense after "what's happening in X?"
    const previous = [...messages].reverse().find((m) => m.role === 'user')?.text || '';

    setMessages((prev) => [...prev, { role: 'user', text: question }]);
    setInput('');
    setSending(true);

    try {
      const res = await fetch(`${SERVER_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: question, previous, city }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not get an answer');

      setMessages((prev) => [
        ...prev,
        { role: 'bot', text: data.answer, sources: data.sources || [], usedAi: data.usedAi },
      ]);
    } catch (error) {
      setMessages((prev) => [...prev, { role: 'bot', text: `⚠️ ${error.message}` }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* FLOATING BUTTON */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[90] bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-5 py-3 rounded-full font-bold shadow-2xl shadow-cyan-500/30 hover:scale-105 transition flex items-center gap-2"
        >
          🤖 <span className="hidden sm:inline">Ask LocalLens</span>
        </button>
      )}

      {/* CHAT PANEL */}
      {open && (
        <div className="fixed bottom-5 right-5 z-[90] w-[92vw] max-w-sm h-[75vh] max-h-[560px] bg-[#0f172a] border border-white/10 rounded-3xl shadow-2xl flex flex-col overflow-hidden text-white">

          {/* header */}
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-black/20">
            <div>
              <p className="font-black text-sm">🤖 LocalLens Assistant</p>
              <p className="text-gray-400 text-[11px]">Answers from real reports · last 7 days</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white text-lg leading-none">
              ✕
            </button>
          </div>

          {/* messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.map((m, idx) => (
              <div key={idx} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                <div className={`max-w-[88%] rounded-2xl px-3 py-2 text-sm ${
                  m.role === 'user'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
                    : 'bg-white/5 border border-white/10 text-gray-100'
                }`}>
                  <p className="whitespace-pre-wrap">{m.text}</p>

                  {/* the real reports behind the answer */}
                  {m.sources && m.sources.length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      <p className="text-[11px] text-gray-400">Based on these reports:</p>
                      {m.sources.map((s) => (
                        <div key={s.id} className="bg-black/30 rounded-xl p-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-semibold">{s.emoji} {s.category} · {s.area}</span>
                          </div>
                          <p className="text-gray-300 text-[11px] mt-0.5">{s.content}</p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${VERDICT_COLORS[s.verdict] || VERDICT_COLORS['Awaiting Verification']}`}>
                              {s.verdict} · {s.trustScore}%
                            </span>
                            {s.severity && s.severity !== 'Unrated' && (
                              <span className="text-[10px] text-gray-400">{s.severity} priority</span>
                            )}
                            <span className="text-[10px] text-gray-400">
                              {s.reportCount} report{s.reportCount > 1 ? 's' : ''}
                            </span>
                            <span className="text-[10px] text-gray-500">
                              {s.createdAt
                                ? new Date(s.createdAt).toLocaleString('en-IN', {
                                    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                                  })
                                : ''}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {sending && (
              <div className="flex justify-start">
                <div className="bg-white/5 border border-white/10 rounded-2xl px-3 py-2 text-sm text-gray-400 animate-pulse">
                  Checking reports...
                </div>
              </div>
            )}

            {/* quick questions, only at the start */}
            {messages.length === 1 && !sending && (
              <div className="flex flex-wrap gap-2 pt-1">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 px-3 py-1.5 rounded-full text-xs hover:bg-cyan-500/20 transition"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <div ref={endRef}></div>
          </div>

          {/* input */}
          <div className="p-3 border-t border-white/10 bg-black/20">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                maxLength={300}
                placeholder="Ask about your area..."
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400"
              />
              <button
                onClick={() => send()}
                disabled={sending || !input.trim()}
                className="bg-cyan-500 px-4 py-2 rounded-xl text-sm font-bold hover:bg-cyan-600 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Send
              </button>
            </div>
            <p className="text-gray-500 text-[10px] mt-2 text-center">
              Community evidence, not an official confirmation. For urgent danger call 112.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

export default ChatBot;